// FinancialPromotionResponseDetails.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { subscribeToFinancialPromotionResponses } from "../services/financialPromotionsRealtime";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import StatusButton from "../../../DesignSystem/Buttons/StatusButton";

import { ReactComponent as IconCopy } from "../../../../assets/icons/financialPromotions/Copy.svg";

import { APP_ROUTES } from "../../../../constants/appRoutes";

import "../../../../css/Marketing/FinancialPromotionContentGenerator/FinancialPromotionResponseDetails.css";
import ApproveButton from "../../../DesignSystem/Buttons/ApproveButton";
import EditButton from "../../../DesignSystem/Buttons/EditButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";

const breadcrumbItems = [
  { label: "Staff Portal", to: APP_ROUTES.DEPARTMENT_ALL_STAFF },
  { label: "Marketing", to: APP_ROUTES.DEPARTMENT_MARKETING },
  {
    label: "Social Media Content Management",
    to: APP_ROUTES.SOCIAL_MEDIA_CONTENT_MANAGEMENT,
  },
  { label: "Content Details" },
];

const toDateObject = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue?.toDate) return dateValue.toDate();

  if (typeof dateValue === "object" && typeof dateValue.seconds === "number") {
    return new Date(dateValue.seconds * 1000);
  }

  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getTimeValue = (dateValue) => {
  const parsedDate = toDateObject(dateValue);
  return parsedDate ? parsedDate.getTime() : 0;
};

const getLatestResponseTime = (response = {}) => {
  return getTimeValue(response.updatedAt || response.createdAt);
};

const formatDateTime = (dateValue) => {
  const parsedDate = toDateObject(dateValue);

  if (!parsedDate) return "Not available";

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getFirstValue = (...values) => {
  return values.find((value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const normaliseResponse = (response = {}) => {
  const content = response.content || {};

  const brief =
    response.brief ||
    response.generationBrief ||
    response.request ||
    response.requestPayload ||
    response.promptDetails ||
    {};

  return {
    ...response,

    id: response.id || response.responseId || "",
    responseId: response.responseId || response.id || "",
    promotionId: response.promotionId || "",

    adviserEmail: getFirstValue(
      response.adviserEmail,
      brief.adviserEmail,
      "Unknown adviser",
    ),
    adviserName: getFirstValue(
      response.adviserName,
      response.name,
      response.adviser,
      response.createdByName,
      brief.adviserName,
      "",
    ),
    tradingName: getFirstValue(
      response.tradingName,
      response.companyName,
      brief.tradingName,
      "",
    ),

    contentType: getFirstValue(response.contentType, brief.contentType, ""),
    contentTypes: toArray(getFirstValue(response.contentTypes, [])),

    platform: getFirstValue(response.platform, brief.platform, ""),
    platforms: toArray(getFirstValue(response.platforms, [])),

    audience: getFirstValue(response.audience, brief.audience, ""),
    objective: getFirstValue(response.objective, brief.objective, ""),
    tone: getFirstValue(response.tone, brief.tone, ""),
    notes: getFirstValue(
      response.notes,
      response.extraNotes,
      response.additionalNotes,
      brief.notes,
      brief.extraNotes,
      "",
    ),

    caption: getFirstValue(response.caption, content.caption, ""),
    shortCaption: getFirstValue(
      response.shortCaption,
      content.shortCaption,
      "",
    ),
    graphicText: getFirstValue(response.graphicText, content.graphicText, ""),
    suggestedImageBrief: getFirstValue(
      response.suggestedImageBrief,
      content.suggestedImageBrief,
      "",
    ),
    regulatoryTextUsed: getFirstValue(
      response.regulatoryTextUsed,
      content.regulatoryTextUsed,
      "",
    ),

    hashtags: toArray(getFirstValue(response.hashtags, content.hashtags, [])),
    complianceNotes: toArray(
      getFirstValue(response.complianceNotes, content.complianceNotes, []),
    ),
    warnings: toArray(getFirstValue(response.warnings, content.warnings, [])),

    status: response.status || "Created",
    hootsuiteStatus: response.hootsuiteStatus || "Not Exported",

    createdBy: response.createdBy || "",
    createdAt: response.createdAt || "",
    updatedBy: response.updatedBy || "",
    updatedAt: response.updatedAt || "",
  };
};

const getPromotionGroupId = (response = {}) => {
  return (
    response.promotionId ||
    `legacy-${response.responseId || response.id || response.storagePath || ""}`
  );
};

const getCaptionWithHashtags = (response = {}) => {
  return [response.caption, response.hashtags?.join(" ")]
    .filter(Boolean)
    .join("\n\n");
};

const getSubmissionPrefillFromResponse = (response = {}) => {
  const hashtagsText = Array.isArray(response.hashtags)
    ? response.hashtags.join(" ")
    : "";

  const contentText = [response.caption, hashtagsText]
    .filter(Boolean)
    .join("\n\n");

  return {
    selectedAdviser: response.adviserEmail || "",
    title: response.objective || "",
    text: contentText,
    contentType: response.contentType || "",
    platform: response.platform || "",
    sourceResponseId: response.id || response.responseId || "",
    sourcePromotionId: response.promotionId || "",
  };
};

const splitToArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const formatCommaSeparatedValue = (value) => {
  const items = splitToArray(value).map(
    (item) => `${item.charAt(0).toUpperCase()}${item.slice(1)}`,
  );

  return items.filter(
    (item, index) =>
      items.findIndex(
        (candidate) => candidate.toLowerCase() === item.toLowerCase(),
      ) === index,
  ).join(", ");
};

const normaliseGeneratorContentTypes = (response = {}) => {
  const rawTypes = [
    ...splitToArray(response.contentTypes),
    ...splitToArray(response.contentType),
  ];

  const mappedTypes = rawTypes.flatMap((type) => {
    const normalised = String(type).toLowerCase().trim();

    if (normalised.includes("mortgage")) return ["mortgage"];

    if (normalised.includes("equity")) return ["equity_release"];

    if (
      normalised.includes("pensions and investments") ||
      normalised.includes("pension and investment") ||
      normalised.includes("investments and pensions") ||
      normalised.includes("investment and pension")
    ) {
      return ["pensions_and_investments"];
    }

    if (normalised.includes("investment")) return ["investments"];

    if (normalised.includes("pension")) return ["pensions"];

    return [];
  });

  return [...new Set(mappedTypes)].filter(Boolean);
};

const normaliseGeneratorPlatforms = (response = {}) => {
  const rawPlatforms = [
    ...splitToArray(response.platforms),
    ...splitToArray(response.platform),
  ];

  const mappedPlatforms = rawPlatforms.flatMap((platform) => {
    const normalised = String(platform).toLowerCase().trim();

    if (normalised.includes("linkedin")) return ["LinkedIn"];
    if (normalised.includes("facebook")) return ["Facebook"];
    if (normalised.includes("instagram")) return ["Instagram"];
    if (normalised === "x" || normalised.includes("twitter")) return ["X"];

    return [];
  });

  return [...new Set(mappedPlatforms)].filter(Boolean);
};

const getExistingResponseFromVersion = (response = {}) => {
  return {
    responseId: response.id || response.responseId || "",
    createdAt: response.createdAt || "",
    updatedAt: response.updatedAt || "",
    status: response.status || "Created",

    caption: response.caption || "",
    shortCaption: response.shortCaption || "",
    graphicText: response.graphicText || "",
    suggestedImageBrief: response.suggestedImageBrief || "",
    regulatoryTextUsed: response.regulatoryTextUsed || "",
    hashtags: response.hashtags || [],
    complianceNotes: response.complianceNotes || [],
    warnings: response.warnings || [],

    contentType: response.contentType || "",
    platform: response.platform || "",
    audience: response.audience || "",
    objective: response.objective || "",
    tone: response.tone || "",
    notes: response.notes || "",
  };
};

const getGeneratorPrefillFromResponse = (response = {}, allResponses = []) => {
  const existingResponses = allResponses.map(getExistingResponseFromVersion);

  return {
    adviserEmail: response.adviserEmail || "",
    contentTypes: normaliseGeneratorContentTypes(response),
    platforms: normaliseGeneratorPlatforms(response),
    audience: response.audience || "",
    objective: response.objective || "",
    tone: response.tone || "Professional, friendly and educational",
    notes: response.notes || "",
    promotionId: response.promotionId || "",
    sourceResponseId: response.id || response.responseId || "",
    sourcePromotionId: response.promotionId || "",

    existingResponses,

    existingResponse:
      existingResponses[0] || getExistingResponseFromVersion(response),
  };
};

const copyToClipboard = async (text) => {
  if (!text) return;

  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error("Failed to copy text:", error);
  }
};

const CopyMiniButton = ({ text }) => {
  if (!text) return null;

  const handleCopy = async () => {
    await copyToClipboard(text);
  };

  return (
    <button
      type="button"
      className="fp-mini-copy-button"
      onClick={handleCopy}
      aria-label="Copy text"
      title="Copy"
    >
      <IconCopy />
    </button>
  );
};

const TextSection = ({ title, value, fallback, copyable = true }) => {
  const displayValue = value || fallback;

  if (!displayValue) return null;

  return (
    <section className="fp-response-content-section">
      <div className="fp-section-title-row">
        <div className="FP_Content_Semibold text-black">{title}</div>

        {copyable && value && <CopyMiniButton text={value} />}
      </div>

      <p className="FP_Content text-black fp-copyable-text">{displayValue}</p>
    </section>
  );
};

const ListSection = ({ title, items = [] }) => {
  if (!items.length) return null;

  return (
    <section className="fp-response-content-section">
      <div className="fp-section-title-row">
        <div className="FP_Content_Semibold text-black">{title}</div>
      </div>

      <ul className="fp-check-list">
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className="FP_Content text-black">
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
};

const DetailItem = ({ label, value }) => {
  return (
    <div className="fp-response-detail-item">
      <span className="FP_Help_Text text-gray">{label}</span>

      <strong className="FP_Content text-black">
        {value || "Not available"}
      </strong>
    </div>
  );
};

const getDisplayStatus = (status) => {
  const normalisedStatus = String(status || "Created").toLowerCase();

  if (normalisedStatus.includes("approved")) return "Approved";
  if (normalisedStatus.includes("rejected")) return "Rejected";
  if (normalisedStatus.includes("pending")) return "Pending";
  if (normalisedStatus.includes("created")) return "Needs Review";

  return status || "Needs Review";
};

const getDisplayStatusColor = (status) => {
  const normalisedStatus = String(status || "Created").toLowerCase();

  if (normalisedStatus.includes("approved")) return "green";
  if (normalisedStatus.includes("rejected")) return "red";
  if (normalisedStatus.includes("pending")) return "pending";
  if (normalisedStatus.includes("created")) return "orange";

  return "orange";
};

const ResponseVersionCard = ({
  response,
  index,
  total,
  onCreateSubmission,
}) => {
  const captionWithHashtags = getCaptionWithHashtags(response);

  return (
    <article className="fp-response-preview-card fp-response-detail-panel">
      <div className="fp-content-feed-header">
        <div>
          <h3 className="FP_Content_Semibold text-black">
            Response {total - index}
          </h3>

          <p className="FP_Help_Text secondary">
            {formatDateTime(response.updatedAt || response.createdAt)}
          </p>
        </div>

        <div className="row gap-s align-center">
          {response.status === "Approved" ? (
            <StatusButton type="Approved" color="green" />
          ) : (
            <>
              {/* <StatusButton
                type={response.status || "Created"}
                color={response.status === "Rejected" ? "red" : "orange"}
              /> */}

              {/* <button
                type="button"
                className="fp-use-response-button FP_Help_Text_Semibold"
                onClick={() => onCreateSubmission(response)}
              >
                Approve Submission
              </button> */}
                <ApproveButton onApprove={() => onCreateSubmission(response)} />
            </>
          )}
        </div>
      </div>

      <TextSection
        title="Caption"
        value={captionWithHashtags}
        fallback="No caption saved."
      />

      <TextSection title="Short Caption" value={response.shortCaption} />

      <TextSection title="Graphic Text" value={response.graphicText} />

      <TextSection title="Image Brief" value={response.suggestedImageBrief} />

      <TextSection
        title="Regulatory Text"
        value={response.regulatoryTextUsed}
        fallback="No regulatory text was saved with this response."
      />
    </article>
  );
};

const FinancialPromotionResponseDetails = () => {
  const { responseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialVersions = useMemo(() => {
    if (Array.isArray(location.state?.versions)) {
      return location.state.versions
        .map(normaliseResponse)
        .sort((a, b) => getLatestResponseTime(b) - getLatestResponseTime(a));
    }

    if (location.state?.response?.versions) {
      return location.state.response.versions
        .map(normaliseResponse)
        .sort((a, b) => getLatestResponseTime(b) - getLatestResponseTime(a));
    }

    if (location.state?.response) {
      return [normaliseResponse(location.state.response)];
    }

    return [];
  }, [location.state]);

  const [versions, setVersions] = useState(initialVersions);
  const [loading, setLoading] = useState(initialVersions.length === 0);
  const [error, setError] = useState("");

  const decodedResponseId = useMemo(() => {
    try {
      return decodeURIComponent(responseId || "");
    } catch {
      return responseId || "";
    }
  }, [responseId]);

  useEffect(() => {
    setLoading(true);
    setError("");

    const unsubscribe = subscribeToFinancialPromotionResponses({
      onData: (data) => {
        const responses = Array.isArray(data)
          ? data.map(normaliseResponse)
          : [];

        const matchedVersions = responses
          .filter((item) => getPromotionGroupId(item) === decodedResponseId)
          .sort((a, b) => getLatestResponseTime(b) - getLatestResponseTime(a));

        if (!matchedVersions.length) {
          setVersions([]);
          setError("Could not find this saved content response.");
          setLoading(false);
          return;
        }

        setVersions(matchedVersions);
        setError("");
        setLoading(false);
      },
      onError: (err) => {
        setError(err.message || "Failed to load content details.");
        setLoading(false);
      },
    });

    return () => {
      unsubscribe?.();
    };
  }, [decodedResponseId]);

  const latestResponse = versions[0] || null;

  const handleCreateSubmissionFromResponse = (response) => {
    const prefill = getSubmissionPrefillFromResponse(response);

    sessionStorage.setItem(
      "financialPromotionSubmissionPrefill",
      JSON.stringify(prefill),
    );

    navigate("/FinancialPromotions/new", {
      state: {
        prefill,
      },
    });
  };

 const handleEditGenerationBrief = (response, allResponses = []) => {
   const generatorPrefill = getGeneratorPrefillFromResponse(
     response,
     allResponses,
   );

   sessionStorage.setItem(
     "financialPromotionGeneratorPrefill",
     JSON.stringify(generatorPrefill),
   );

   navigate(APP_ROUTES.SOCIAL_MEDIA_CONTENT_CREATION, {
     state: {
       generatorPrefill,
     },
   });
 };
  return (
    <div className="main-content">
      <Sidebar />

      <Helmet>
        <title>Content Details</title>

        <meta
          name="description"
          content="Review generated social media content and the full brief used to generate it."
        />
      </Helmet>

      <main className="Main-Container gap-xl " style={{ flexDirection: "column" }}>
 
        <Breadcrumb items={breadcrumbItems} />

        <section className="fp-social-dashboard-header gap-xl fit-column py-s align-left flex-start">
          <div>
            <h3 className="FP_H3 primary">
              Content Details
            </h3>

            <p className="FP_Help_Text secondary">
              View all responses generated for this promotion. The latest
              response is shown first.
            </p>
          </div>
        </section>

        {loading && (
          <p className="FP_Help_Text secondary">Loading content details...</p>
        )}

        {error && <div className="fp-error FP_Content">{error}</div>}

        {!loading && latestResponse && (
          <section className="fp-response-detail-grid">
            <div className="fp-response-detail-main column gap-l">
              {versions.map((version, index) => (
                <ResponseVersionCard
                  key={
                    version.responseId ||
                    version.id ||
                    version.storagePath ||
                    `${version.createdAt}-${index}`
                  }
                  response={version}
                  index={index}
                  total={versions.length}
                  onCreateSubmission={handleCreateSubmissionFromResponse}
                />
              ))}

              <article className="fp-response-preview-card fp-response-detail-panel">
                <div className="fp-card-header">
                  <div>
                    <h2 className="FP_Subtitle primary">
                      Compliance Notes & Warnings
                    </h2>

                    <p className="FP_Help_Text secondary">
                      Compliance notes and reviewer warnings saved with the
                      latest response.
                    </p>
                  </div>
                </div>

                <ListSection
                  title="Compliance Notes"
                  items={latestResponse.complianceNotes}
                />

                <ListSection
                  title="Reviewer Warnings"
                  items={latestResponse.warnings}
                />
              </article>
            </div>

            <aside className="fp-response-detail-sidebar">
              <article className="fp-response-brief-card">
                <div className="fp-card-header">
                  <strong className="FP_Content_Semibold text-black">
                    Generation Brief
                  </strong>

                  <EditButton
                    onEdit={() => handleEditGenerationBrief(latestResponse, versions)}
                    label="Edit Brief"
                  />
                </div>
                <div className="fp-response-detail-list">
                  <DetailItem
                    label="Adviser Email"
                    value={latestResponse.adviserEmail}
                  />

                  <DetailItem
                    label="Trading Name"
                    value={latestResponse.tradingName}
                  />

                  <DetailItem
                    label="Content Type"
                    value={formatCommaSeparatedValue(
                      latestResponse.contentType,
                    )}
                  />

                  <DetailItem
                    label="Platform"
                    value={formatCommaSeparatedValue(latestResponse.platform)}
                  />

                  <DetailItem
                    label="Audience"
                    value={latestResponse.audience}
                  />

                  <DetailItem
                    label="Objective"
                    value={latestResponse.objective}
                  />

                  <DetailItem label="Tone" value={latestResponse.tone} />

                  <DetailItem
                    label="Extra Notes"
                    value={latestResponse.notes}
                  />

                  <DetailItem
                    label="Total Responses"
                    value={String(versions.length)}
                  />
                </div>
              </article>
            </aside>
          </section>
        )}
      </main>
    </div>
  );
};

export default FinancialPromotionResponseDetails;
